import { useCallback, useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { IntegrationsService } from "@propertelligent/client-api";
import { currentUrl } from "../../../utils/helper";
import { GetImages } from "../../../utils/GetImages";
import Card from "../../../newComponents/card";
import { isElectron, desktop } from "../../../utils/electron/desktop";
import { PubSub, TopicsEnum, NotificationType } from "../../../utils/pubsub";
import { ConfirmationPopup } from "../../../newComponents/confirmation-popup/confirmation-popup-demo";
import SimpleButton from "../../../newComponents/buttons/Simple-button";
import AddTraspButton from "../../firstTimeUserExperience/commonCom/AddTraspButton";
import {
  getStatus,
  disconnect as disconnectOrgEmail,
  saveSmtpSettings,
  testSmtpConnection,
  getMicrosoftClientId,
  EmailProviderType,
  OrganizationEmailStatus,
  SmtpSettingsRequest,
} from "./organizationEmailApi";
import { buildGmailOAuthRedirect, ORG_GMAIL_OAUTH_STATE } from "./oauthRedirect";
import { buildMicrosoftOAuthRedirect, ORG_OUTLOOK_OAUTH_STATE, OUTLOOK_SEND_SCOPES } from "../../../utils/microsoftOAuth";

// Org-Admin-only "connect/manage" screen for the organization's own email account — the sender used for every
// organization-branded email once connected (see .claude/context/emails.md). Two mutually-exclusive provider
// options: Gmail (OAuth) or a custom SMTP account. Deliberately narrow: no inbox/compose here, that stays on the
// personal /messaging/email screen. Backend endpoints (OrganizationEmailController) are not yet in
// @propertelligent/client-api as of this change, so status/connect/disconnect go through the raw-fetch bridge in
// organizationEmailApi.ts — swap to the generated OrganizationEmailService once the package is regenerated.

// Send-oriented scope set for the organization sender account: full-mailbox + send + profile. This is the
// narrowed set needed to send organization-branded email as this account; it is NOT identical to the personal
// Gmail connection's scopes (see .claude/context/emails.md).
const ORG_GMAIL_SEND_SCOPES =
  "https://mail.google.com/ https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/userinfo.profile";

const EMPTY_SMTP_FORM: SmtpSettingsRequest = {
  host: "",
  port: 587,
  username: "",
  password: "",
  useSsl: false,
  fromName: "",
  fromEmail: "",
};

const OrganizationEmailSettings = () => {
  const [status, setStatus] = useState<OrganizationEmailStatus | null>(null);
  const [clientId, setClientId] = useState("");
  const [microsoftClientId, setMicrosoftClientId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  // Provider picker + SMTP form state — only relevant while nothing is connected yet.
  const [selectedProvider, setSelectedProvider] = useState<"gmail" | "smtp" | null>(null);
  const [smtpForm, setSmtpForm] = useState<SmtpSettingsRequest>(EMPTY_SMTP_FORM);
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDisconnectConfirmOpen, setIsDisconnectConfirmOpen] = useState(false);

  const loadStatus = useCallback(() => {
    setIsLoading(true);
    getStatus()
      .then(setStatus)
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    loadStatus();
    IntegrationsService.postApiIntegrationsKeysGmailClientId()
      .then((res) => setClientId(res?.success))
      .catch((error) => console.error(error));
    getMicrosoftClientId().then(setMicrosoftClientId);
  }, [loadStatus]);

  const oauthSignIn = async () => {
    const { endpoint, params, authUrl } = buildGmailOAuthRedirect({
      clientId,
      redirectUri: `${currentUrl}/callback/gmail`,
      scopes: ORG_GMAIL_SEND_SCOPES,
      state: ORG_GMAIL_OAUTH_STATE,
    });

    if (isElectron && desktop?.gmailOAuth) {
      const res = await desktop.gmailOAuth(authUrl);
      if (res?.code) {
        window.location.href = `/callback/gmail?code=${encodeURIComponent(res.code)}&state=${ORG_GMAIL_OAUTH_STATE}`;
      } else if (res?.error && res.error !== "closed") {
        console.error("Organization Gmail OAuth failed:", res.error);
      }
      return;
    }

    const form = document.createElement("form");
    form.setAttribute("method", "GET");
    form.setAttribute("action", endpoint);
    for (const p in params) {
      const input = document.createElement("input");
      input.setAttribute("type", "hidden");
      input.setAttribute("name", p);
      input.setAttribute("value", params[p]);
      form.appendChild(input);
    }
    document.body.appendChild(form);
    form.submit();
  };

  const outlookOauthSignIn = async () => {
    if (!microsoftClientId) {
      console.error("Microsoft client id is not yet available; cannot start Outlook OAuth.");
      PubSub.publish(TopicsEnum.Notification.toString(), {
        type: NotificationType.Failure,
        title: "Failure",
        message: "Unable to start Outlook sign-in right now. Please try again in a moment.",
      });
      return;
    }

    const { endpoint, params, authUrl } = buildMicrosoftOAuthRedirect({
      clientId: microsoftClientId,
      redirectUri: `${currentUrl}/callback/outlook`,
      scopes: OUTLOOK_SEND_SCOPES,
      state: ORG_OUTLOOK_OAUTH_STATE,
    });

    if (isElectron && desktop?.gmailOAuth) {
      const res = await desktop.gmailOAuth(authUrl);
      if (res?.code) {
        window.location.href = `/callback/outlook?code=${encodeURIComponent(res.code)}&state=${ORG_OUTLOOK_OAUTH_STATE}`;
      } else if (res?.error && res.error !== "closed") {
        console.error("Organization Outlook OAuth failed:", res.error);
      }
      return;
    }

    const form = document.createElement("form");
    form.setAttribute("method", "GET");
    form.setAttribute("action", endpoint);
    for (const p in params) {
      const input = document.createElement("input");
      input.setAttribute("type", "hidden");
      input.setAttribute("name", p);
      input.setAttribute("value", params[p]);
      form.appendChild(input);
    }
    document.body.appendChild(form);
    form.submit();
  };

  const handleDisconnect = () => {
    setIsDisconnectConfirmOpen(false);
    setIsDisconnecting(true);
    disconnectOrgEmail()
      .then(() => {
        setSelectedProvider(null);
        setSmtpForm(EMPTY_SMTP_FORM);
        loadStatus();
        PubSub.publish(TopicsEnum.Notification.toString(), {
          type: NotificationType.Success,
          title: "Success",
          message: "Organization email account disconnected.",
        });
      })
      .catch((error) => {
        console.error("Failed to disconnect organization email account:", error);
        PubSub.publish(TopicsEnum.Notification.toString(), {
          type: NotificationType.Failure,
          title: "Failure",
          message: "Unable to disconnect the organization email account. Please try again.",
        });
      })
      .finally(() => setIsDisconnecting(false));
  };

  const updateSmtpField = <K extends keyof SmtpSettingsRequest>(field: K, value: SmtpSettingsRequest[K]) => {
    setSmtpForm((prev) => ({ ...prev, [field]: value }));
  };

  // Mirrors the backend's SaveSmtpSettingsAsync/TestConnectionAsync ValidateRequest checks — keeps Test/Connect
  // disabled until the form can't fail on an empty-field validation error, instead of round-tripping to the
  // server just to learn that.
  const isSmtpFormValid =
    smtpForm.host.trim() !== "" &&
    smtpForm.port > 0 &&
    smtpForm.username.trim() !== "" &&
    smtpForm.password.trim() !== "" &&
    smtpForm.fromEmail.trim() !== "";

  const handleTestSmtp = () => {
    setIsTesting(true);
    testSmtpConnection(smtpForm)
      .then(() => {
        PubSub.publish(TopicsEnum.Notification.toString(), {
          type: NotificationType.Success,
          title: "Success",
          message: "Test email sent successfully.",
        });
      })
      .catch((error) => {
        console.error("SMTP test connection failed:", error);
        PubSub.publish(TopicsEnum.Notification.toString(), {
          type: NotificationType.Failure,
          title: "Failure",
          message: "Test failed — check your settings and try again.",
        });
      })
      .finally(() => setIsTesting(false));
  };

  const handleSaveSmtp = () => {
    setIsSaving(true);
    saveSmtpSettings(smtpForm)
      .then(() => {
        setSelectedProvider(null);
        setSmtpForm(EMPTY_SMTP_FORM);
        loadStatus();
        PubSub.publish(TopicsEnum.Notification.toString(), {
          type: NotificationType.Success,
          title: "Success",
          message: "Organization SMTP account connected.",
        });
      })
      .catch((error) => {
        console.error("Failed to save organization SMTP settings:", error);
        PubSub.publish(TopicsEnum.Notification.toString(), {
          type: NotificationType.Failure,
          title: "Failure",
          message: "Unable to save SMTP settings. Please check the values and try again.",
        });
      })
      .finally(() => setIsSaving(false));
  };

  const renderSmtpForm = () => (
    <div className="d-flex flex-column gap-2">
      <Form>
        <Row>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3" controlId="orgSmtpHost">
              <Form.Label className="sub_text grey_subtext1">SMTP Host</Form.Label>
              <Form.Control
                className="sub_text grey_subtext1"
                type="text"
                placeholder="Example: smtp.yourdomain.com"
                value={smtpForm.host}
                onChange={(e) => updateSmtpField("host", e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3" controlId="orgSmtpPort">
              <Form.Label className="sub_text grey_subtext1">Port</Form.Label>
              <Form.Control
                className="sub_text grey_subtext1"
                type="number"
                placeholder="587"
                value={smtpForm.port}
                onChange={(e) => updateSmtpField("port", Number(e.target.value))}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3" controlId="orgSmtpUsername">
              <Form.Label className="sub_text grey_subtext1">Username</Form.Label>
              <Form.Control
                className="sub_text grey_subtext1"
                type="text"
                placeholder="Enter User Name"
                value={smtpForm.username}
                onChange={(e) => updateSmtpField("username", e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3" controlId="orgSmtpPassword">
              <Form.Label className="sub_text grey_subtext1">Password</Form.Label>
              <Form.Control
                className="sub_text grey_subtext1"
                type="password"
                autoComplete="new-password"
                placeholder="Enter Password"
                value={smtpForm.password}
                onChange={(e) => updateSmtpField("password", e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
        {/* Plain, self-contained markup (no react-bootstrap Form.Check / .form-check-label) -- matches
            AddGmailAccount.tsx's personal SMTP form, which sidesteps an ambient global style that clips/overlaps
            .form-check-label on wrap. */}
        <div className="mb-3" style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
          <input
            type="checkbox"
            id="orgSmtpUseSsl"
            checked={smtpForm.useSsl}
            onChange={(e) => updateSmtpField("useSsl", e.target.checked)}
            style={{ flexShrink: 0, marginTop: 3 }}
          />
          <label
            htmlFor="orgSmtpUseSsl"
            className="sub_text grey_subtext1"
            style={{ flex: "1 1 0%", minWidth: 0, marginBottom: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          >
            Use SSL (typically port 465; leave unchecked for STARTTLS on port 587)
          </label>
        </div>
        <Row>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3" controlId="orgSmtpFromName">
              <Form.Label className="sub_text grey_subtext1">From Name</Form.Label>
              <Form.Control
                className="sub_text grey_subtext1"
                type="text"
                placeholder="Example: Acme Property Management"
                value={smtpForm.fromName}
                onChange={(e) => updateSmtpField("fromName", e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3" controlId="orgSmtpFromEmail">
              <Form.Label className="sub_text grey_subtext1">From Email</Form.Label>
              <Form.Control
                className="sub_text grey_subtext1"
                type="email"
                placeholder="Enter From Email"
                value={smtpForm.fromEmail}
                onChange={(e) => updateSmtpField("fromEmail", e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
      </Form>

      <div className="d-flex justify-content-end align-items-center gap-3 pt-3" style={{ borderTop: "1px solid var(--Grey-Divider, #e5e7eb)" }}>
        <AddTraspButton
          type="button"
          bname="Cancel"
          onClick={() => {
            setSelectedProvider(null);
            setSmtpForm(EMPTY_SMTP_FORM);
          }}
          width="fit-content"
        />
        <SimpleButton
          label="Test"
          onClick={handleTestSmtp}
          isDisabled={!isSmtpFormValid}
          isLoading={isTesting}
        />
        <SimpleButton
          label="Connect"
          onClick={handleSaveSmtp}
          isDisabled={!isSmtpFormValid}
          isLoading={isSaving}
        />
      </div>

      <div className="normal_text grey_subtext1" style={{ fontSize: "13px", lineHeight: 1.6 }}>
        <p className="fw-600 black" style={{ marginBottom: 4 }}>Common provider settings:</p>
        <p style={{ margin: 0 }}>
          <strong>Gmail / Google Workspace:</strong> Host <code>smtp.gmail.com</code>, Port <code>587</code> (SSL
          unchecked). You'll need an{" "}
          <a
            href="https://support.google.com/mail/answer/185833"
            target="_blank"
            rel="noopener noreferrer"
          >
            App Password
          </a>{" "}
          — your normal login password will not work.
        </p>
        <p style={{ margin: 0 }}>
          <strong>Microsoft 365 / Outlook:</strong> Host <code>smtp.office365.com</code>, Port <code>587</code> (SSL
          unchecked).
        </p>
        <p style={{ margin: 0 }}>
          Not sure what to enter? Ask whoever manages your email/domain (IT admin or email provider) for your
          organization's SMTP host, port, username, and password.
        </p>
      </div>
    </div>
  );

  const renderProviderPicker = () => (
    <div className="d-flex flex-column gap-3">
      <p className="normal_text grey_subtext1">Not connected.</p>
      {selectedProvider === "smtp" ? (
        renderSmtpForm()
      ) : (
        <div className="d-flex flex-column gap-2">
          <button type="button" className="emailConnectButton" onClick={oauthSignIn}>
            <img src={GetImages.Google} alt="GoogleIcon" className="styleicon" />
            <p className="normal_text fw-600 text-start emailConnectButtonLabel">Connect with Gmail</p>
          </button>
          <button type="button" className="emailConnectButton" onClick={outlookOauthSignIn}>
            <img src={GetImages.Outlook} alt="OutlookIcon" height={24} width={24} />
            <p className="normal_text fw-600 text-start emailConnectButtonLabel">Connect with Outlook</p>
          </button>
          <button type="button" className="emailConnectButton" onClick={() => setSelectedProvider("smtp")}>
            <img src={GetImages.Email} alt="EmailIcon" height={24} width={24} />
            <p className="normal_text fw-600 text-start emailConnectButtonLabel">Connect a Custom SMTP Account</p>
          </button>
        </div>
      )}
    </div>
  );

  const renderConnected = () => (
    <div className="d-flex flex-column gap-2">
      <div className="d-flex align-items-center gap-2">
        <img src={GetImages.Email} alt="Connected" height={24} width={24} />
        {status?.providerType === EmailProviderType.Gmail ? (
          <p className="normal_text fw-600 black">
            Connected as {status.emailAddress ?? "an organization Gmail account"}
          </p>
        ) : status?.providerType === EmailProviderType.Office365 ? (
          <p className="normal_text fw-600 black">
            Connected as {status.emailAddress ?? "an organization Outlook account"}
          </p>
        ) : (
          <p className="normal_text fw-600 black">
            Connected via SMTP as {status?.smtpUsername ?? "unknown user"} on {status?.smtpHost ?? "unknown host"},
            sending as {status?.fromName ?? status?.fromEmail ?? "the organization"}
          </p>
        )}
      </div>
      <SimpleButton
        label="Disconnect"
        onClick={() => setIsDisconnectConfirmOpen(true)}
        isLoading={isDisconnecting}
      />
      <ConfirmationPopup
        isOpen={isDisconnectConfirmOpen}
        title="Disconnect Email Account"
        message="Are you sure you want to disconnect the organization's connected email account? Organization-branded emails will fall back to the platform default until a new account is connected."
        primaryButtonLabel="Disconnect"
        secondaryButtonLabel="Cancel"
        onPrimaryAction={handleDisconnect}
        onSecondaryAction={() => setIsDisconnectConfirmOpen(false)}
        onClose={() => setIsDisconnectConfirmOpen(false)}
        isPrimaryDestructive={true}
      />
    </div>
  );

  return (
    <div className="organizationContentContainer d-flex flex-column gap-3 p-4">
      <h4 className="main_heading black">Organization Email Account</h4>
      <p className="normal_text grey_subtext1">
        Connect a Gmail account, an Outlook account, or a custom SMTP account for this organization. Once connected, every
        organization-branded email (invites, payment reminders, lease notices, and other automated
        communications) sends from this account instead of the platform default. Only one provider can be
        connected at a time.
      </p>
      <Card>
        {isLoading ? (
          <p className="normal_text">Loading...</p>
        ) : status?.isConnected ? (
          renderConnected()
        ) : (
          renderProviderPicker()
        )}
      </Card>
    </div>
  );
};

export default OrganizationEmailSettings;
